import { useState, useEffect } from 'react';
import { useUser } from './useUser';

export interface UseUserAdminReturn {
  isAdmin: boolean;
  loading: boolean;
  error: string;
}

/**
 * Platform administration is carried in signed Firebase custom claims. Client-editable
 * Firestore documents must never be used as an authorization source.
 */
export const useUserAdmin = (): UseUserAdminReturn => {
  const user = useUser();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [checkedUserId, setCheckedUserId] = useState<string>();

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setLoading(false);
      setCheckedUserId(undefined);
      return;
    }

    setLoading(true);
    setError('');

    let canceled = false;
    void user.getIdTokenResult().then(
      (result) => {
        if (canceled) return;
        setIsAdmin(result.claims.admin === true || result.claims.platformAdmin === true);
      },
      (err) => {
        if (canceled) return;
        setError(err instanceof Error ? err.message : 'Could not verify administrator access.');
        setIsAdmin(false);
      },
    ).finally(() => {
      if (!canceled) {
        setCheckedUserId(user.uid);
        setLoading(false);
      }
    });

    return () => {
      canceled = true;
    };
  }, [user]);

  return { isAdmin, loading: loading || (!!user && checkedUserId !== user.uid), error };
};
